import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-client';
import { z } from 'zod';

import { TEAMMATES, type ReferralAssignee } from './referrals-store';

/**
 * Teammate directory hook (SCRUM-312/343): GET /api/users mapped onto the
 * picker's ReferralAssignee shape. The API projection deliberately carries no
 * emails (PII-free unauthenticated surface), so API-sourced teammates have
 * `email: ''` and carry `userId` for referral routing instead. Falls back to
 * the client TEAMMATES roster while loading or when the API is unreachable
 * (offline preview) — the pickers stay functional either way.
 */
const USERS_URL = '/api/users';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

// SCRUM-643: tenant derived server-side from the App ID token claim (SCRUM-102).

const userSummarySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  role: z.string(),
});

async function fetchTeammates(): Promise<ReferralAssignee[]> {
  const res = await apiFetch(USERS_URL);
  if (!res.ok) {
    throw new Error(`GET ${USERS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return userSummarySchema
    .array()
    .parse(raw)
    .map((u) => ({ name: u.displayName, email: '', role: u.role, userId: u.id }));
}

/**
 * Returns the teammate roster: API-sourced when available, the client demo
 * roster otherwise. Never empty, never loading-blocked.
 */
export function useTeammates(): readonly ReferralAssignee[] {
  const { data } = useQuery<ReferralAssignee[]>({
    queryKey: ['teammates'],
    queryFn: fetchTeammates,
    staleTime: Infinity,
    retry: false,
    throwOnError: false,
  });
  return data && data.length > 0 ? data : TEAMMATES;
}
